import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { Anchor, Box, Center, Group, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconFileTypePdf } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import {
  GetReceiptsRequestParams,
  ReceiptsSortBy,
  useGetReceiptsCountQuery,
  useGetReceiptsQuery,
} from '@api/receipts.api';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import { Receipt } from '@interfaces/receipts.interface';

export default function Receipts() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const selectedReceipt = searchParams.get('sel');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetReceiptsRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Receipt>>({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as ReceiptsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================

  // Get initial Receipts count
  const {
    data: receiptsCount = { count: 0 },
    isLoading: isLoadingReceiptsCount,
  } = useGetReceiptsCountQuery(searchQuery);

  const { data = [], isLoading } = useGetReceiptsQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Scontrini',
    });
  }, [setLayoutProps]);

  return isLoading || isLoadingReceiptsCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder="Cerca numero scontrino"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessuno scontrino trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedReceipt && 'bold') || undefined}>
                {dateToDateString(new Date(record.date))}
              </Box>
            ),
          },
          {
            accessor: 'id',
            title: 'Numero scontrino',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedReceipt && 'bold') || undefined}>
                {record.id}
              </Box>
            ),
          },
          {
            accessor: 'url',
            title: 'Pdf',
            render: (record) => (
              <Anchor
                href={record.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <IconFileTypePdf />
              </Anchor>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={receiptsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
